import { useEffect, useMemo, useState } from 'react';

import type { SessionPresence, SessionUser } from '../live/api.ts';
import { useLive } from '../live/context.tsx';
import {
  litCapabilities,
  navItemIsActive,
  navItems,
  rememberCapabilities,
  rememberedCapabilities,
  type NavEntry,
} from '../lib/nav.ts';
import { VIEWS, VIEW_LABEL, type ViewName } from '../lib/router.ts';
import {
  AgentsIcon,
  BoardIcon,
  GraphIcon,
  OpsIcon,
  RunsIcon,
  ReviewQueueIcon,
  BudgetsIcon,
  SettingsIcon,
  HelpIcon,
} from './icons.tsx';
import { FontPicker } from './font-picker.tsx';
import { ThemePicker } from './theme-picker.tsx';

const RAIL_ICON: Partial<Record<ViewName, typeof BoardIcon>> = {
  board: BoardIcon,
  review: ReviewQueueIcon,
  graph: GraphIcon,
  agents: AgentsIcon,
  ops: OpsIcon,
  runs: RunsIcon,
  budgets: BudgetsIcon,
  settings: SettingsIcon,
  help: HelpIcon,
};

export function NavRail({
  view,
  user,
  presence,
  onGo,
  onSignOut,
}: {
  view: ViewName;
  user: SessionUser | null;
  presence: SessionPresence[];
  onGo: (view: ViewName) => void;
  onSignOut: () => void;
}) {
  const state = useLive();
  const snapshot = state.snapshot;
  const scope = state.projectSlug;
  const capabilities = useMemo(
    () => (snapshot ? litCapabilities(snapshot.projects, scope) : rememberedCapabilities(scope)),
    [snapshot, scope],
  );
  useEffect(() => {
    if (!snapshot) return;
    if (scope !== null && snapshot.projectSlug !== scope) return;
    rememberCapabilities(scope, capabilities);
  }, [snapshot, scope, capabilities]);

  const groups = navItems(VIEWS, capabilities);

  const online =
    user !== null &&
    (presence.some((entry) => entry.userId === user.id) || state.connection === 'live');

  return (
    <nav className="nav nav-rail type-fixed" aria-label="Views">
      <div className="nav-items">
        {groups.rail.map((item) => (
          <RailItem key={item.view} item={item} active={navItemIsActive(item, view)} onGo={onGo} />
        ))}
      </div>

      <div className="nav-foot">
        <ThemePicker />
        <FontPicker />
        {groups.account.map((item) => (
          <RailItem key={item.view} item={item} active={navItemIsActive(item, view)} onGo={onGo} />
        ))}

        <div className="nav-user-chip" title={user ? `${user.displayName} (${user.role})` : 'Local session'}>
          <span className={`nav-user-avatar${online ? ' is-online' : ''}`} aria-hidden>
            {(user?.displayName ?? 'N').slice(0, 1).toUpperCase()}
          </span>
          <span className="nav-user-meta">
            <span className="nav-user-name">{user?.displayName ?? 'Local'}</span>
            <span className={`nav-user-presence${online ? ' is-online' : ''}`}>
              {user ? (online ? 'Online' : 'Away') : 'auth off'}
            </span>
          </span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm nav-signout" onClick={onSignOut}>
          Sign out
        </button>
        {state.snapshot?.health.version ? (
          <span className="nav-version" title={state.snapshot.health.version}>
            {state.snapshot.health.version.split('+')[0]}
          </span>
        ) : null}
      </div>
    </nav>
  );
}

function RailItem({
  item,
  active,
  onGo,
}: {
  item: NavEntry;
  active: boolean;
  onGo: (view: ViewName) => void;
}) {
  const Icon = RAIL_ICON[item.view] ?? BoardIcon;
  const [dismissed, setDismissed] = useState(false);
  const hintId = `nav-hint-${item.view}`;
  return (
    <div
      className="nav-hint-anchor"
      data-dismissed={dismissed || undefined}
      onPointerEnter={() => setDismissed(false)}
      onFocus={() => setDismissed(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDismissed(true);
      }}
    >
      <button
        type="button"
        className="nav-item nav-rail-item"
        aria-current={active ? 'page' : undefined}
        aria-label={VIEW_LABEL[item.view]}
        aria-describedby={hintId}
        onClick={() => {
          setDismissed(true);
          onGo(item.view);
        }}
      >
        <Icon size={16} />
        <span>{VIEW_LABEL[item.view]}</span>
      </button>
      <span className="nav-hint" id={hintId} role="tooltip">{item.hint}</span>
    </div>
  );
}
